import { ChangeDetectorRef, Component, Inject, effect, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { Property } from '../../../models/property';
import { take } from 'rxjs';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentTemplate } from '../../../models/documentTemplate';
import { DraftLegalDocumentRequest, InteractivePlaceholderContent } from '../../../requests/draftLegalDocumentRequest';
import { PropertyService } from '../../../services/PropertyService';
import { FileResponse } from '../../../responses/fileResponse';
import { base64ToBlob } from '../../../helpers/blob.utils';
import { openLegalDocumentDialog } from '../legal-document-dialog.opener';
import { ExecuteLegalDocumentRequest } from '../../../requests/executeLegalDocumentRequest';
import { formatDate } from '@angular/common';
import { VaultDocumentsResponse } from '../../../responses/vaultDocumentsResponse';
import { TransformationService } from '../../../services/TransformationService';

@Component({ standalone: false,
  selector: 'app-buyer-legal-document',
  templateUrl: 'buyer-legal-document.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['buyer-legal-document.component.scss']
})
export class BuyerLegalDocumentComponent {
  readonly internal = input<boolean>(false);
  readonly documentTemplate = input<DocumentTemplate>();

  readonly onDocumentExecuted = output<any>();

  public confirmButtonText = "Preview Agreement";

  public busy:boolean=false;
  public property!:Property;

  public placeholderForm:FormGroup;

  public minDate:string;

  public tempInteractivePlaceholderContentList:InteractivePlaceholderContent[]=[];

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private transformationService:TransformationService, private propertyService:PropertyService, private dialog: MatDialog, private cdRef:ChangeDetectorRef) {
    this.placeholderForm = new FormGroup({
      placeholders: new FormArray<FormGroup>([])
    });
    effect(() => {
      this.addPlaceholders();
    });
    if(data){
      this.property=data.property;
    }
    let now = new Date()
    let tomorrow = new Date(now.setDate(now.getDate()+1));
    this.minDate = formatDate(new Date(tomorrow.getFullYear(),tomorrow.getMonth(),tomorrow.getDate(),23,59,0),'yyyy-MM-ddTHH:mm','en-US');
  }

  get canPreviewDocument(){
    return this.placeholderForm.valid;
  }

  get canPreviewBlankDocument(){
    return this.documentTemplate()?.typeId==5; //only for purchase agreement
  }

  get placeholdersArray(): FormArray {
    return this.placeholderForm.controls.placeholders as FormArray;
  }

  public onPreviewDocument(){
    //create a pdf preview, show in document pop up dialog and let user either approve/sign or cancel
    this.busy=true;
    let timeStamp = new Date();
    let request = new DraftLegalDocumentRequest(); //either generalize or use dedicated prepurchase request
    //we need to add the interactive placeholders with content here
    const placeholders = this.placeholderForm.get('placeholders') as FormArray;
    placeholders.value.forEach((p:any)=>{
      let ipc = new InteractivePlaceholderContent();
      ipc.placeholderName=p.name;
      ipc.placeholderContent= this.convertPlaceholderValue(p.name, p.value);
      request.interactivePlaceholderContentList.push(ipc);
    });
    this.tempInteractivePlaceholderContentList = request.interactivePlaceholderContentList;
    request.propertyId=this.property.id;
    request.text=this.documentTemplate()!.text;
    request.watermark=this.documentTemplate()!.watermarkText;
    let day = timeStamp.getDate();
    request.day = day.toString();
    request.month = timeStamp.toLocaleString('default', { month: 'long' });
    request.year = timeStamp.getFullYear().toString();
    this.propertyService.draftBuyerAgreementForAuction(request).pipe(take(1)).subscribe(r=>this.processDocumentDraftResult(r, false));
  }

  public onPreviewBlankClick(){
    //create a pdf preview, show in document pop up dialog and let user either approve/sign or cancel
    this.busy=true;
    this.propertyService.getPropertyAbstracts(false)
    //for now just take first one for testing
    let timeStamp = new Date();
    let request = new DraftLegalDocumentRequest(); //either generalize or use dedicated prepurchase request
    //we need to add the interactive placeholders with content here
    const placeholders = this.placeholderForm.get('placeholders') as FormArray;
    placeholders.value.forEach((p:any) =>{
      let ipc = new InteractivePlaceholderContent();
      ipc.placeholderName=p.name;
      ipc.placeholderContent='[' + p.label + ']'; //just use label as content for preview
      request.interactivePlaceholderContentList.push(ipc);
    });
    request.propertyId=this.property.id;
    request.text=this.documentTemplate()!.text;
    request.watermark=this.documentTemplate()!.watermarkText;
    let day = timeStamp.getDate();
    request.day = day.toString();
    request.month = timeStamp.toLocaleString('default', { month: 'long' });
    request.year = timeStamp.getFullYear().toString();
    this.propertyService.draftBuyerAgreementForAuction(request).pipe(take(1)).subscribe(r=>this.processDocumentDraftResult(r, true));
  }


  private processDocumentDraftResult(response:FileResponse, blankPreview:boolean=false){
    this.busy=false;
    if(response.success) this.documentDraftDialog(response.base64File!, blankPreview);
  }
  
  private documentDraftDialog(base64File:string, blankPreview:boolean){
    base64ToBlob(base64File,'application/pdf').then(blob=>{
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.data={pdfFile:blob, previewOnly:blankPreview};
      dialogConfig.minHeight=600;
      dialogConfig.minWidth=800;

      openLegalDocumentDialog(this.dialog, dialogConfig).pipe(take(1)).subscribe(value=>{
        if(value){
          this.busy=true;
          this.executeDocument(base64File);
        }
      });
    });
  }

  private executeDocument(base64File:string){
    let request = new ExecuteLegalDocumentRequest();
    request.propertyId=this.property.id;
    request.auctionId=this.property.auction?.id;
    request.scheduleSeqNo=this.property.auction?.scheduleSeqNo;
    request.documentTypeId=this.documentTemplate()!.typeId;
    request.base64File = base64File;
    request.datafields = JSON.stringify(this.tempInteractivePlaceholderContentList);
    this.propertyService.executeBuyerAgreementForAuction(request).pipe(take(1)).subscribe(r=>this.processExecuteDocumentResult(r));
  }

  private processExecuteDocumentResult(response:VaultDocumentsResponse){
    this.busy=false;
    //update parent dialog to show current authorization status by raising an event
    this.onDocumentExecuted.emit(response);
  }

  //#region placeholders
  private addPlaceholders() {
    if(this.documentTemplate()?.interactivePlaceholders.length){
      const fa = this.placeholderForm.get('placeholders') as FormArray;
      this.documentTemplate()!.interactivePlaceholders.forEach(p=>{
        if(!p.internal || (p.internal && this.internal())){
          //only use placeholders if the 'internal' flags add up
          let parameter=this.createPlaceholder(p.name, p.inputType, undefined, p.inputOptionsArray); 
          fa.push(parameter);
        }
      });
      this.cdRef.detectChanges();
    }
  }

  private createPlaceholder(name:string, inputType:string, defaultValue:string|undefined, inputOptionArray:any[]|undefined): FormGroup {
    let fg =  new FormGroup({
       name: new FormControl(name), //don't show in ui; serves as link between parameter and default value of parameter
       value: new FormControl(defaultValue)
    });
    fg.get('value')!.addValidators(Validators.required);
    if(inputType=='bool' && defaultValue==undefined)fg.get('value')!.setValue('false');
    if(inputType=='pct') fg.get('value')!.addValidators(Validators.max(100));
    //if(inputType=='opt' && inputOptionArray.length>0) fg.get('value').setValue(inputOptionArray[0]);
    fg.updateValueAndValidity();
    return fg
  }

  public getPlaceholderLabel(name:string){
    return this.documentTemplate()?.interactivePlaceholders?.find(p=>p.name==name)?.label;
  }
  
  public getPlaceholderDescription(name:string){
    return this.documentTemplate()?.interactivePlaceholders?.find(p=>p.name==name)?.description;
  }
  
  public getPlaceholderInputType(name:string){
    return this.documentTemplate()?.interactivePlaceholders?.find(p=>p.name==name)?.inputType;
  }
  
  public getPlaceholderInputOptions(name:string){
    const param=this.documentTemplate()?.interactivePlaceholders?.find(p=>p.name==name);
    return param?.inputOptionsArray;
  }
  
  private convertPlaceholderValue(name:string,value:any){
    let inputType = this.getPlaceholderInputType(name);
    if(inputType==='num'){
      return value.toString();
    }
    else if(inputType==='cur'){
      return this.transformationService.transformAmount(value,true);
    }
    else if(inputType==='pct'){
      return this.transformationService.transformPercent(value);
    }
    else if(inputType==='dte'){
      return this.transformationService.transformDate(value);
    }
    else{
      return value;
    }
  }
  //#endregion

}



